/**
 * Iter 118 — Maintenance Size Tiers bulk-edit grid for the
 * Component Rates page.
 *
 * Lists every (service · surface · tier) row stored in the
 * `maintenance_size_tiers` collection (36 rows in the standard
 * seed) in a compact table the office can scan + edit in one go.
 * Each row exposes:
 *
 *   Service · Surface · Tier · Range (min–max dim) ·
 *   Flat rate $ · Materials $ · Hours · Labour $/hr · Contractor $/hr ·
 *   Build-sum reconciliation ·  Save
 *
 * Saving a row hits the same PATCH endpoint as the back-calc modal
 * (PATCH /maintenance-builder/size-tiers/{service}/{tier}), so the
 * two surfaces stay in lock-step. When the operator changes the
 * flat rate + materials + hours but leaves labour $/hr blank, the
 * backend auto-derives it from the office's 80/20 own-labour vs
 * sub-contractor split.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, Save, AlertCircle, RefreshCw, Filter, ChevronDown, ChevronRight } from 'lucide-react';

const TIER_ORDER  = { small: 0, medium: 1, large: 2 };
const TIER_LABEL  = { small: 'Small', medium: 'Medium', large: 'Large' };

const SERVICE_LABEL = {
  roof_treatment:     'Roof Treatment',
  roof_wash:          'Roof Wash',
  house_wash:         'House Wash',
  gutter_clean:       'Gutter Clean',
  driveway_clean:     'Driveway Clean',
  driveway_treatment: 'Driveway Treatment',
};

const SURFACE_LABEL = {
  '':                'No surface',
  iron:              'Iron',
  modern_concrete:   'Modern Concrete',
  old_concrete:      'Old / Clay Concrete',
  tin_tile:          'Tin Tile',
};

export default function MaintenanceSizeTiersSection({ api }) {
  const [open, setOpen]       = useState(false);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [flash, setFlash]     = useState(null);
  const [drafts, setDrafts]   = useState({});         // { rowKey: { flat_rate, materials_cost, ... } }
  const [savingKey, setSavingKey] = useState(null);
  const [filterSvc, setFilterSvc] = useState('all');
  const [filterSurf, setFilterSurf] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get('/maintenance-builder/size-tiers');
      const sorted = [...(data.rows || [])].sort((a, b) => {
        if (a.service_key !== b.service_key) return a.service_key.localeCompare(b.service_key);
        if ((a.surface || '') !== (b.surface || '')) return (a.surface || '').localeCompare(b.surface || '');
        return (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99);
      });
      setRows(sorted);
      setDrafts({});
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const rowKey = (r) => `${r.service_key}::${r.surface || ''}::${r.tier}`;

  const visibleRows = useMemo(() => rows.filter(r =>
    (filterSvc  === 'all' || r.service_key === filterSvc) &&
    (filterSurf === 'all' || (r.surface || '') === filterSurf)
  ), [rows, filterSvc, filterSurf]);

  const services = useMemo(() => Array.from(new Set(rows.map(r => r.service_key))), [rows]);
  const surfaces = useMemo(() => Array.from(new Set(rows.map(r => r.surface || ''))), [rows]);

  const patchRow = (r, patch) => {
    const k = rowKey(r);
    setDrafts(prev => ({ ...prev, [k]: { ...(prev[k] || {}), ...patch } }));
  };

  const effective = (r) => ({ ...r, ...(drafts[rowKey(r)] || {}) });

  const buildSum = (r) => {
    const e = effective(r);
    return round2((Number(e.materials_cost) || 0)
                + (Number(e.labour_hours) || 0) * (Number(e.labour_rate_per_hr) || 0)
                + (Number(e.labour_hours) || 0) * (Number(e.contractor_rate_per_hr) || 0));
  };

  const isDirty = (r) => !!drafts[rowKey(r)];

  const saveRow = async (r) => {
    const k = rowKey(r);
    setSavingKey(k);
    setFlash(null);
    setErr(null);
    try {
      const e = effective(r);
      await api.patch(`/maintenance-builder/size-tiers/${r.service_key}/${r.tier}`, {
        surface:               r.surface || '',
        flat_rate:             Number(e.flat_rate) || 0,
        materials_cost:        Number(e.materials_cost) || 0,
        buy_cost:              Number(e.buy_cost) || 0,
        labour_hours:          Number(e.labour_hours) || 0,
        labour_rate_per_hr:    Number(e.labour_rate_per_hr) || 0,
        contractor_rate_per_hr: Number(e.contractor_rate_per_hr) || 0,
        min_dim:               Number(e.min_dim) || 0,
        max_dim:               Number(e.max_dim) || 0,
      });
      setFlash({ tone: 'ok', msg: `${SERVICE_LABEL[r.service_key] || r.service_key} · ${TIER_LABEL[r.tier]} saved ✓` });
      // Refresh the row in-place so subsequent renders agree with
      // whatever the backend chose (e.g. auto-derived labour $/hr).
      const { data } = await api.get('/maintenance-builder/size-tiers');
      const next = (data.rows || []).find(x => x.service_key === r.service_key && (x.surface || '') === (r.surface || '') && x.tier === r.tier);
      if (next) {
        setRows(prev => prev.map(x => (rowKey(x) === k ? next : x)));
        setDrafts(prev => { const cp = { ...prev }; delete cp[k]; return cp; });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden"
         data-testid="maintenance-size-tiers-section">
      <button type="button"
              onClick={() => setOpen(o => !o)}
              data-testid="maintenance-size-tiers-toggle"
              className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-cyan-50 to-teal-50 border-b border-slate-200 hover:from-cyan-100 hover:to-teal-100 transition-colors">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-cyan-700" /> : <ChevronRight size={14} className="text-cyan-700" />}
          <span className="text-sm font-extrabold tracking-tight text-slate-900 font-[Manrope]">Maintenance · Size-Tier Rates</span>
          <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-700 bg-white border border-cyan-300 rounded-full px-2 py-0.5">
            {rows.length} rows
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          Roof Treatment · Roof Wash · House Wash · Gutter Clean · Driveway Clean &amp; Treatment — flat rates per Small / Medium / Large size tier.
        </span>
      </button>

      {open && (
        <div className="p-4">
          {/* Filter strip + Refresh */}
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-slate-500">
              <Filter size={11} /> Service
            </div>
            <select value={filterSvc} onChange={(e) => setFilterSvc(e.target.value)}
                    data-testid="maintenance-tiers-filter-service"
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              <option value="all">All services</option>
              {services.map(s => <option key={s} value={s}>{SERVICE_LABEL[s] || s}</option>)}
            </select>
            <div className="flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-slate-500">
              Surface
            </div>
            <select value={filterSurf} onChange={(e) => setFilterSurf(e.target.value)}
                    data-testid="maintenance-tiers-filter-surface"
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              <option value="all">All surfaces</option>
              {surfaces.map(s => <option key={s} value={s}>{SURFACE_LABEL[s] || s}</option>)}
            </select>
            <button type="button" onClick={load} disabled={loading}
                    data-testid="maintenance-tiers-refresh"
                    className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-[11px] font-bold uppercase tracking-wider rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
            </button>
          </div>

          {/* Flash / error banners */}
          {err && (
            <div className="mb-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 flex items-center gap-1.5"
                 data-testid="maintenance-tiers-error">
              <AlertCircle size={12} /> {err}
            </div>
          )}
          {flash?.tone === 'ok' && (
            <div className="mb-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2"
                 data-testid="maintenance-tiers-flash">
              {flash.msg}
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="text-sm text-slate-500 py-6 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading 36 tier rows…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="maintenance-tiers-table">
                <thead className="text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-2">Service</th>
                    <th className="text-left py-2 pr-2">Surface</th>
                    <th className="text-left py-2 pr-2">Tier</th>
                    <th className="text-center py-2 px-1 w-24">Range (dim)</th>
                    <th className="text-right py-2 px-1.5">Flat $</th>
                    <th className="text-right py-2 px-1.5">Materials $</th>
                    <th className="text-right py-2 px-1.5 bg-amber-50/40" title="Supplier / wholesale cost — internal margin tracking only">Buy $</th>
                    <th className="text-right py-2 px-1.5">Hours</th>
                    <th className="text-right py-2 px-1.5">Labour $/hr</th>
                    <th className="text-right py-2 px-1.5">Contractor $/hr</th>
                    <th className="text-right py-2 px-1.5 bg-cyan-50">Builds to</th>
                    <th className="py-2 pl-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map(r => {
                    const k    = rowKey(r);
                    const e    = effective(r);
                    const sum  = buildSum(r);
                    const flat = Number(e.flat_rate) || 0;
                    const mismatch = Math.abs(sum - flat) > 0.5;
                    const dirty = isDirty(r);
                    return (
                      <tr key={k}
                          className={`border-b border-slate-100 ${dirty ? 'bg-amber-50/40' : ''}`}
                          data-testid={`maintenance-tier-row-${k}`}>
                        <td className="py-1.5 pr-2 font-semibold text-slate-800">{SERVICE_LABEL[r.service_key] || r.service_key}</td>
                        <td className="py-1.5 pr-2 text-slate-600">{SURFACE_LABEL[r.surface || ''] || (r.surface || '—')}</td>
                        <td className="py-1.5 pr-2">
                          <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-cyan-100 text-cyan-800 border border-cyan-300">
                            {TIER_LABEL[r.tier] || r.tier}
                          </span>
                        </td>
                        <td className="py-1.5 px-1 text-center text-slate-500">
                          <Range r={r} e={e} onChange={(p) => patchRow(r, p)} />
                        </td>
                        <NumCell value={e.flat_rate}     onChange={(v) => patchRow(r, { flat_rate: v })}     prefix="$" testid={`maintenance-tier-flat_rate-${k}`} />
                        <NumCell value={e.materials_cost} onChange={(v) => patchRow(r, { materials_cost: v })} prefix="$" testid={`maintenance-tier-materials_cost-${k}`} />
                        <NumCell value={e.buy_cost} onChange={(v) => patchRow(r, { buy_cost: v })} prefix="$" testid={`maintenance-tier-buy_cost-${k}`} />
                        <NumCell value={e.labour_hours}  onChange={(v) => patchRow(r, { labour_hours: v })}  testid={`maintenance-tier-labour_hours-${k}`} />
                        <NumCell value={e.labour_rate_per_hr}     onChange={(v) => patchRow(r, { labour_rate_per_hr: v })}     prefix="$" testid={`maintenance-tier-labour_rate_per_hr-${k}`} />
                        <NumCell value={e.contractor_rate_per_hr} onChange={(v) => patchRow(r, { contractor_rate_per_hr: v })} prefix="$" testid={`maintenance-tier-contractor_rate_per_hr-${k}`} />
                        <td className={`py-1.5 px-1.5 text-right font-mono-num font-bold ${mismatch ? 'bg-amber-100 text-amber-800' : 'bg-cyan-50 text-cyan-800'}`}
                            title={mismatch ? `Build-sum differs from flat by $${Math.abs(sum - flat).toFixed(2)}` : 'Materials + labour + contractor reconciles with flat rate'}
                            data-testid={`maintenance-tier-buildsum-${k}`}>
                          ${sum.toFixed(2)}
                        </td>
                        <td className="py-1.5 pl-2">
                          <button type="button"
                                  onClick={() => saveRow(r)}
                                  disabled={!dirty || savingKey === k}
                                  data-testid={`maintenance-tier-save-${k}`}
                                  className={`px-2 py-1 text-[11px] font-bold uppercase tracking-wider rounded inline-flex items-center gap-1 ${
                                    dirty ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                  }`}>
                            {savingKey === k ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={11} className="py-4 text-center text-slate-400">No rows match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
              <div className="mt-2 text-[10px] text-slate-500 italic">
                Builds-to column = Materials + Labour hours × Labour $/hr + Labour hours × Contractor $/hr. Highlighted amber when it doesn't reconcile with the flat rate. Saving a row with blank labour $/hr asks the backend to auto-derive it from the 80/20 own-labour vs sub-contractor split.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Atoms ─────────────────────────────────────────────────────── */

function NumCell({ value, onChange, prefix, testid }) {
  return (
    <td className="py-1.5 px-1">
      <div className="relative">
        {prefix && <span className="absolute left-1 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{prefix}</span>}
        <input type="number" min="0" step="0.01" value={value ?? 0}
               onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
               data-testid={testid}
               className={`w-20 ${prefix ? 'pl-4' : 'pl-1.5'} pr-1.5 py-1 text-xs border border-slate-300 rounded text-right font-mono-num focus:outline-none focus:ring-1 focus:ring-emerald-400 focus:border-emerald-400`} />
      </div>
    </td>
  );
}

function Range({ r, e, onChange }) {
  return (
    <div className="inline-flex items-center gap-0.5 text-[10px] font-mono-num">
      <input type="number" min="0" step="1" value={e.min_dim ?? 0}
             onChange={(ev) => onChange({ min_dim: ev.target.value === '' ? 0 : parseFloat(ev.target.value) })}
             data-testid={`maintenance-tier-min_dim-${r.service_key}::${r.surface || ''}::${r.tier}`}
             className="w-10 px-0.5 py-0.5 border border-slate-200 rounded text-right" />
      <span className="text-slate-400">–</span>
      <input type="number" min="0" step="1" value={e.max_dim ?? 0}
             onChange={(ev) => onChange({ max_dim: ev.target.value === '' ? 0 : parseFloat(ev.target.value) })}
             data-testid={`maintenance-tier-max_dim-${r.service_key}::${r.surface || ''}::${r.tier}`}
             className="w-10 px-0.5 py-0.5 border border-slate-200 rounded text-right" />
      <span className="text-slate-400 ml-0.5">{r.unit}</span>
    </div>
  );
}

function round2(n) { return Math.round((Number(n) || 0) * 100) / 100; }
